import React, { useState, useEffect, useRef } from "react";
import {
  X,
  DollarSign,
  RefreshCw,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowLeftRight,
  Search,
  TrendingUp,
} from "lucide-react";
import { ref, get, set } from "firebase/database";
import { db } from "../firebase";
import "./CurrencyConverter.css";
import { useLanguage } from "../i18n/LanguageContext";

const API_KEY = "de4b5e74f9684f4447adeabd";
const API_URL = `https://v6.exchangerate-api.com/v6/${API_KEY}/latest/USD`;
const CACHE_TTL_MS = 3 * 24 * 60 * 60 * 1000; // 3 days in ms
const DB_CACHE_PATH = "currency_cache/USD";

// ── All supported currencies with full names (bilingual) ──────────────────────
const CURRENCY_META = {
  AED: { name: "درهم إماراتي", nameEn: "UAE Dirham", flag: "🇦🇪" },
  AFN: { name: "أفغاني أفغاني", nameEn: "Afghan Afghani", flag: "🇦🇫" },
  ALL: { name: "ليك ألباني", nameEn: "Albanian Lek", flag: "🇦🇱" },
  AMD: { name: "درام أرميني", nameEn: "Armenian Dram", flag: "🇦🇲" },
  ANG: { name: "غيلدر هولندي أنتيلي", nameEn: "Netherlands Antillean Guilder", flag: "🇳🇱" },
  AOA: { name: "كوانزا أنغولي", nameEn: "Angolan Kwanza", flag: "🇦🇴" },
  ARS: { name: "بيسو أرجنتيني", nameEn: "Argentine Peso", flag: "🇦🇷" },
  AUD: { name: "دولار أسترالي", nameEn: "Australian Dollar", flag: "🇦🇺" },
  AWG: { name: "فلورين أروبي", nameEn: "Aruban Florin", flag: "🇦🇼" },
  AZN: { name: "مانات أذربيجاني", nameEn: "Azerbaijani Manat", flag: "🇦🇿" },
  BAM: { name: "مارك بوسني", nameEn: "Bosnia-Herzegovina Mark", flag: "🇧🇦" },
  BBD: { name: "دولار بربادوسي", nameEn: "Barbadian Dollar", flag: "🇧🇧" },
  BDT: { name: "تاكا بنغلاديشي", nameEn: "Bangladeshi Taka", flag: "🇧🇩" },
  BGN: { name: "ليف بلغاري", nameEn: "Bulgarian Lev", flag: "🇧🇬" },
  BHD: { name: "دينار بحريني", nameEn: "Bahraini Dinar", flag: "🇧🇭" },
  BIF: { name: "فرنك بوروندي", nameEn: "Burundian Franc", flag: "🇧🇮" },
  BMD: { name: "دولار برمودي", nameEn: "Bermudian Dollar", flag: "🇧🇲" },
  BND: { name: "دولار بروناي", nameEn: "Brunei Dollar", flag: "🇧🇳" },
  BOB: { name: "بوليفيانو بوليفي", nameEn: "Bolivian Boliviano", flag: "🇧🇴" },
  BRL: { name: "ريال برازيلي", nameEn: "Brazilian Real", flag: "🇧🇷" },
  BSD: { name: "دولار باهامي", nameEn: "Bahamian Dollar", flag: "🇧🇸" },
  BTN: { name: "نغولتروم بوتاني", nameEn: "Bhutanese Ngultrum", flag: "🇧🇹" },
  BWP: { name: "بولا بوتسواني", nameEn: "Botswanan Pula", flag: "🇧🇼" },
  BYN: { name: "روبل بيلاروسي", nameEn: "Belarusian Ruble", flag: "🇧🇾" },
  BZD: { name: "دولار بليزي", nameEn: "Belize Dollar", flag: "🇧🇿" },
  CAD: { name: "دولار كندي", nameEn: "Canadian Dollar", flag: "🇨🇦" },
  CDF: { name: "فرنك كونغولي", nameEn: "Congolese Franc", flag: "🇨🇩" },
  CHF: { name: "فرنك سويسري", nameEn: "Swiss Franc", flag: "🇨🇭" },
  CLP: { name: "بيسو تشيلي", nameEn: "Chilean Peso", flag: "🇨🇱" },
  CNY: { name: "يوان صيني", nameEn: "Chinese Yuan", flag: "🇨🇳" },
  COP: { name: "بيسو كولومبي", nameEn: "Colombian Peso", flag: "🇨🇴" },
  CRC: { name: "كولون كوستاريكي", nameEn: "Costa Rican Colón", flag: "🇨🇷" },
  CUP: { name: "بيسو كوبي", nameEn: "Cuban Peso", flag: "🇨🇺" },
  CVE: { name: "إسكودو الرأس الأخضر", nameEn: "Cape Verdean Escudo", flag: "🇨🇻" },
  CZK: { name: "كرونة تشيكية", nameEn: "Czech Koruna", flag: "🇨🇿" },
  DJF: { name: "فرنك جيبوتي", nameEn: "Djiboutian Franc", flag: "🇩🇯" },
  DKK: { name: "كرونة دنماركية", nameEn: "Danish Krone", flag: "🇩🇰" },
  DOP: { name: "بيسو دومينيكاني", nameEn: "Dominican Peso", flag: "🇩🇴" },
  DZD: { name: "دينار جزائري", nameEn: "Algerian Dinar", flag: "🇩🇿" },
  EGP: { name: "جنيه مصري", nameEn: "Egyptian Pound", flag: "🇪🇬" },
  ERN: { name: "ناكفا إريتري", nameEn: "Eritrean Nakfa", flag: "🇪🇷" },
  ETB: { name: "بير إثيوبي", nameEn: "Ethiopian Birr", flag: "🇪🇹" },
  EUR: { name: "يورو", nameEn: "Euro", flag: "🇪🇺" },
  FJD: { name: "دولار فيجي", nameEn: "Fijian Dollar", flag: "🇫🇯" },
  FKP: { name: "جنيه فوكلاند", nameEn: "Falkland Islands Pound", flag: "🇫🇰" },
  FOK: { name: "كرونة فيرو", nameEn: "Faroese Króna", flag: "🇫🇴" },
  GBP: { name: "جنيه إسترليني", nameEn: "British Pound Sterling", flag: "🇬🇧" },
  GEL: { name: "لاري جورجي", nameEn: "Georgian Lari", flag: "🇬🇪" },
  GGP: { name: "جنيه غيرنزي", nameEn: "Guernsey Pound", flag: "🇬🇬" },
  GHS: { name: "سيدي غاني", nameEn: "Ghanaian Cedi", flag: "🇬🇭" },
  GIP: { name: "جنيه جبل طارق", nameEn: "Gibraltar Pound", flag: "🇬🇮" },
  GMD: { name: "دلاسي غامبي", nameEn: "Gambian Dalasi", flag: "🇬🇲" },
  GNF: { name: "فرنك غيني", nameEn: "Guinean Franc", flag: "🇬🇳" },
  GTQ: { name: "كيتزال غواتيمالي", nameEn: "Guatemalan Quetzal", flag: "🇬🇹" },
  GYD: { name: "دولار غياني", nameEn: "Guyanaese Dollar", flag: "🇬🇾" },
  HKD: { name: "دولار هونج كونج", nameEn: "Hong Kong Dollar", flag: "🇭🇰" },
  HNL: { name: "ليمبيرا هندوراسي", nameEn: "Honduran Lempira", flag: "🇭🇳" },
  HRK: { name: "كونا كرواتية", nameEn: "Croatian Kuna", flag: "🇭🇷" },
  HTG: { name: "غورد هايتي", nameEn: "Haitian Gourde", flag: "🇭🇹" },
  HUF: { name: "فورنت مجري", nameEn: "Hungarian Forint", flag: "🇭🇺" },
  IDR: { name: "روبية إندونيسية", nameEn: "Indonesian Rupiah", flag: "🇮🇩" },
  ILS: { name: "شيكل إسرائيلي", nameEn: "Israeli New Shekel", flag: "🇮🇱" },
  IMP: { name: "جنيه جزيرة مان", nameEn: "Isle of Man Pound", flag: "🇮🇲" },
  INR: { name: "روبية هندية", nameEn: "Indian Rupee", flag: "🇮🇳" },
  IQD: { name: "دينار عراقي", nameEn: "Iraqi Dinar", flag: "🇮🇶" },
  IRR: { name: "ريال إيراني", nameEn: "Iranian Rial", flag: "🇮🇷" },
  ISK: { name: "كرونة آيسلندية", nameEn: "Icelandic Króna", flag: "🇮🇸" },
  JEP: { name: "جنيه جيرسي", nameEn: "Jersey Pound", flag: "🇯🇪" },
  JMD: { name: "دولار جامايكي", nameEn: "Jamaican Dollar", flag: "🇯🇲" },
  JOD: { name: "دينار أردني", nameEn: "Jordanian Dinar", flag: "🇯🇴" },
  JPY: { name: "ين ياباني", nameEn: "Japanese Yen", flag: "🇯🇵" },
  KES: { name: "شلن كيني", nameEn: "Kenyan Shilling", flag: "🇰🇪" },
  KGS: { name: "سوم قيرغيزستاني", nameEn: "Kyrgystani Som", flag: "🇰🇬" },
  KHR: { name: "رييل كمبودي", nameEn: "Cambodian Riel", flag: "🇰🇭" },
  KID: { name: "دولار كيريباتي", nameEn: "Kiribati Dollar", flag: "🇰🇮" },
  KMF: { name: "فرنك جزر القمر", nameEn: "Comorian Franc", flag: "🇰🇲" },
  KRW: { name: "وون كوري جنوبي", nameEn: "South Korean Won", flag: "🇰🇷" },
  KWD: { name: "دينار كويتي", nameEn: "Kuwaiti Dinar", flag: "🇰🇼" },
  KYD: { name: "دولار جزر كايمان", nameEn: "Cayman Islands Dollar", flag: "🇰🇾" },
  KZT: { name: "تنغي كازاخستاني", nameEn: "Kazakhstani Tenge", flag: "🇰🇿" },
  LAK: { name: "كيب لاوسي", nameEn: "Laotian Kip", flag: "🇱🇦" },
  LBP: { name: "جنيه لبناني", nameEn: "Lebanese Pound", flag: "🇱🇧" },
  LKR: { name: "روبية سريلانكية", nameEn: "Sri Lankan Rupee", flag: "🇱🇰" },
  LRD: { name: "دولار ليبيري", nameEn: "Liberian Dollar", flag: "🇱🇷" },
  LSL: { name: "لوتي ليسوتي", nameEn: "Lesotho Loti", flag: "🇱🇸" },
  LYD: { name: "دينار ليبي", nameEn: "Libyan Dinar", flag: "🇱🇾" },
  MAD: { name: "درهم مغربي", nameEn: "Moroccan Dirham", flag: "🇲🇦" },
  MDL: { name: "ليو مولدوفي", nameEn: "Moldovan Leu", flag: "🇲🇩" },
  MGA: { name: "أرياري مدغشقري", nameEn: "Malagasy Ariary", flag: "🇲🇬" },
  MKD: { name: "دينار مقدوني", nameEn: "Macedonian Denar", flag: "🇲🇰" },
  MMK: { name: "كيات ميانماري", nameEn: "Myanmar Kyat", flag: "🇲🇲" },
  MNT: { name: "توغروغ منغولي", nameEn: "Mongolian Tugrik", flag: "🇲🇳" },
  MOP: { name: "باتاكا ماكاوي", nameEn: "Macanese Pataca", flag: "🇲🇴" },
  MRU: { name: "أوقية موريتانية", nameEn: "Mauritanian Ouguiya", flag: "🇲🇷" },
  MUR: { name: "روبية موريشيوسية", nameEn: "Mauritian Rupee", flag: "🇲🇺" },
  MVR: { name: "روفية مالديفية", nameEn: "Maldivian Rufiyaa", flag: "🇲🇻" },
  MWK: { name: "كواتشا مالاوية", nameEn: "Malawian Kwacha", flag: "🇲🇼" },
  MXN: { name: "بيسو مكسيكي", nameEn: "Mexican Peso", flag: "🇲🇽" },
  MYR: { name: "رينغت ماليزي", nameEn: "Malaysian Ringgit", flag: "🇲🇾" },
  MZN: { name: "متيكال موزمبيقي", nameEn: "Mozambican Metical", flag: "🇲🇿" },
  NAD: { name: "دولار ناميبي", nameEn: "Namibian Dollar", flag: "🇳🇦" },
  NGN: { name: "نيرة نيجيرية", nameEn: "Nigerian Naira", flag: "🇳🇬" },
  NIO: { name: "كوردوبا نيكاراغوي", nameEn: "Nicaraguan Córdoba", flag: "🇳🇮" },
  NOK: { name: "كرونة نرويجية", nameEn: "Norwegian Krone", flag: "🇳🇴" },
  NPR: { name: "روبية نيبالية", nameEn: "Nepalese Rupee", flag: "🇳🇵" },
  NZD: { name: "دولار نيوزيلندي", nameEn: "New Zealand Dollar", flag: "🇳🇿" },
  OMR: { name: "ريال عماني", nameEn: "Omani Rial", flag: "🇴🇲" },
  PAB: { name: "بالبوا بنمي", nameEn: "Panamanian Balboa", flag: "🇵🇦" },
  PEN: { name: "سول بيروي", nameEn: "Peruvian Sol", flag: "🇵🇪" },
  PGK: { name: "كينا بابوا غينيا الجديدة", nameEn: "Papua New Guinean Kina", flag: "🇵🇬" },
  PHP: { name: "بيسو فلبيني", nameEn: "Philippine Peso", flag: "🇵🇭" },
  PKR: { name: "روبية باكستانية", nameEn: "Pakistani Rupee", flag: "🇵🇰" },
  PLN: { name: "زلوتي بولندي", nameEn: "Polish Zloty", flag: "🇵🇱" },
  PYG: { name: "غواراني باراغوياني", nameEn: "Paraguayan Guarani", flag: "🇵🇾" },
  QAR: { name: "ريال قطري", nameEn: "Qatari Rial", flag: "🇶🇦" },
  RON: { name: "ليو روماني", nameEn: "Romanian Leu", flag: "🇷🇴" },
  RSD: { name: "دينار صربي", nameEn: "Serbian Dinar", flag: "🇷🇸" },
  RUB: { name: "روبل روسي", nameEn: "Russian Ruble", flag: "🇷🇺" },
  RWF: { name: "فرنك رواندي", nameEn: "Rwandan Franc", flag: "🇷🇼" },
  SAR: { name: "ريال سعودي", nameEn: "Saudi Riyal", flag: "🇸🇦" },
  SBD: { name: "دولار جزر سليمان", nameEn: "Solomon Islands Dollar", flag: "🇸🇧" },
  SCR: { name: "روبية سيشلية", nameEn: "Seychellois Rupee", flag: "🇸🇨" },
  SDG: { name: "جنيه سوداني", nameEn: "Sudanese Pound", flag: "🇸🇩" },
  SEK: { name: "كرونة سويدية", nameEn: "Swedish Krona", flag: "🇸🇪" },
  SGD: { name: "دولار سنغافوري", nameEn: "Singapore Dollar", flag: "🇸🇬" },
  SHP: { name: "جنيه سانت هيلينا", nameEn: "Saint Helena Pound", flag: "🇸🇭" },
  SLE: { name: "ليوني سيراليوني", nameEn: "Sierra Leonean Leone", flag: "🇸🇱" },
  SOS: { name: "شلن صومالي", nameEn: "Somali Shilling", flag: "🇸🇴" },
  SRD: { name: "دولار سورينامي", nameEn: "Surinamese Dollar", flag: "🇸🇷" },
  SSP: { name: "جنيه جنوب السودان", nameEn: "South Sudanese Pound", flag: "🇸🇸" },
  STN: { name: "دوبرا ساو تومي", nameEn: "São Tomé & Príncipe Dobra", flag: "🇸🇹" },
  SYP: { name: "جنيه سوري", nameEn: "Syrian Pound", flag: "🇸🇾" },
  SZL: { name: "ليلانغيني سوازيلاندي", nameEn: "Swazi Lilangeni", flag: "🇸🇿" },
  THB: { name: "بات تايلاندي", nameEn: "Thai Baht", flag: "🇹🇭" },
  TJS: { name: "سوموني طاجيكي", nameEn: "Tajikistani Somoni", flag: "🇹🇯" },
  TMT: { name: "مانات تركماني", nameEn: "Turkmenistani Manat", flag: "🇹🇲" },
  TND: { name: "دينار تونسي", nameEn: "Tunisian Dinar", flag: "🇹🇳" },
  TOP: { name: "باآنغا تونغي", nameEn: "Tongan Paʻanga", flag: "🇹🇴" },
  TRY: { name: "ليرة تركية", nameEn: "Turkish Lira", flag: "🇹🇷" },
  TTD: { name: "دولار ترينيداد", nameEn: "Trinidad & Tobago Dollar", flag: "🇹🇹" },
  TVD: { name: "دولار توفالو", nameEn: "Tuvaluan Dollar", flag: "🇹🇻" },
  TWD: { name: "دولار تايواني", nameEn: "New Taiwan Dollar", flag: "🇹🇼" },
  TZS: { name: "شلن تنزاني", nameEn: "Tanzanian Shilling", flag: "🇹🇿" },
  UAH: { name: "هريفنيا أوكرانية", nameEn: "Ukrainian Hryvnia", flag: "🇺🇦" },
  UGX: { name: "شلن أوغندي", nameEn: "Ugandan Shilling", flag: "🇺🇬" },
  USD: { name: "دولار أمريكي", nameEn: "US Dollar", flag: "🇺🇸" },
  UYU: { name: "بيسو أوروغواياني", nameEn: "Uruguayan Peso", flag: "🇺🇾" },
  UZS: { name: "سوم أوزبكي", nameEn: "Uzbekistani Som", flag: "🇺🇿" },
  VES: { name: "بوليفار فنزويلي", nameEn: "Venezuelan Bolívar", flag: "🇻🇪" },
  VND: { name: "دونغ فيتنامي", nameEn: "Vietnamese Dong", flag: "🇻🇳" },
  VUV: { name: "فاتو فانواتو", nameEn: "Vanuatu Vatu", flag: "🇻🇺" },
  WST: { name: "تالا ساموا", nameEn: "Samoan Tala", flag: "🇼🇸" },
  XAF: { name: "فرنك وسط أفريقيا CFA", nameEn: "Central African CFA Franc", flag: "🌍" },
  XCD: { name: "دولار شرق الكاريبي", nameEn: "East Caribbean Dollar", flag: "🌎" },
  XDR: { name: "حقوق السحب الخاصة", nameEn: "Special Drawing Rights", flag: "🌐" },
  XOF: { name: "فرنك غرب أفريقيا CFA", nameEn: "West African CFA Franc", flag: "🌍" },
  XPF: { name: "فرنك المحيط الهادئ CFP", nameEn: "CFP Franc", flag: "🌊" },
  YER: { name: "ريال يمني", nameEn: "Yemeni Rial", flag: "🇾🇪" },
  ZAR: { name: "راند جنوب أفريقي", nameEn: "South African Rand", flag: "🇿🇦" },
  ZMW: { name: "كواتشا زامبي", nameEn: "Zambian Kwacha", flag: "🇿🇲" },
  ZWL: { name: "Zimbabwean Dollar", nameEn: "Zimbabwean Dollar", flag: "🇿🇼" },
};

// Popular currencies shown first in list
const POPULAR_CURRENCIES = [
  "EGP","SAR","AED","EUR","GBP","TRY","MAD","JOD","KWD","QAR",
  "OMR","BHD","LYD","TND","DZD","IQD","LBP","SYP","YER","SDG",
  "AUD","CAD","CHF","CNY","JPY","INR","RUB","MXN","BRL","KRW",
  "SGD","HKD","NOK","SEK","DKK","PLN","CZK","HUF","NGN","ZAR",
];

// ── Fetch rates from API ──────────────────────────────────────────────────────
async function fetchRatesFromAPI() {
  const response = await fetch(API_URL);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  if (data.result !== "success") throw new Error("API error");
  return data.conversion_rates;
}

// ── Get rates: try Firebase cache first, then API ────────────────────────────
async function getRates(forceRefresh = false) {
  const cacheRef = ref(db, DB_CACHE_PATH);

  if (!forceRefresh) {
    try {
      const snap = await get(cacheRef);
      const cached = snap.val();
      if (cached && cached.rates && cached.cachedAt) {
        const age = Date.now() - cached.cachedAt;
        if (age < CACHE_TTL_MS) {
          return { rates: cached.rates, fromCache: true, cachedAt: cached.cachedAt };
        }
      }
    } catch (_) {}
  }

  // Fetch fresh from API
  const rates = await fetchRatesFromAPI();
  const payload = { rates, cachedAt: Date.now() };
  try {
    await set(cacheRef, payload);
  } catch (_) {}
  return { rates, fromCache: false, cachedAt: Date.now() };
}

// ── Format large numbers nicely ──────────────────────────────────────────────
function formatResult(value, lang) {
  if (value === null || value === undefined || isNaN(value)) return "—";
  const locale = lang === "en" ? "en-US" : "ar-EG";
  if (value >= 1000000) return value.toLocaleString(locale, { maximumFractionDigits: 2 });
  if (value >= 100) return value.toLocaleString(locale, { maximumFractionDigits: 3 });
  return value.toLocaleString(locale, { maximumFractionDigits: 4 });
}

function timeSince(ms, lang) {
  const diff = Date.now() - ms;
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (lang === "en") {
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    const mins = Math.floor(diff / 60000);
    return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  }
  if (days > 0) return `منذ ${days} يوم`;
  if (hours > 0) return `منذ ${hours} ساعة`;
  const mins = Math.floor(diff / 60000);
  return `منذ ${mins} دقيقة`;
}

// ── Currency Dropdown ─────────────────────────────────────────────────────────
const CurrencyDropdown = ({ selected, rates, onSelect, onClose, lang }) => {
  const [search, setSearch] = useState("");

  const allCodes = rates ? Object.keys(rates).filter((c) => c !== "USD") : [];

  // Build sorted list: popular first, then rest alphabetically
  const popularInRates = POPULAR_CURRENCIES.filter((c) => allCodes.includes(c));
  const others = allCodes.filter((c) => !POPULAR_CURRENCIES.includes(c)).sort();
  const ordered = [...popularInRates, ...others];

  const filtered = ordered.filter((code) => {
    const q = search.toLowerCase();
    const meta = CURRENCY_META[code];
    const displayName = lang === "en" ? (meta?.nameEn || meta?.name || code) : (meta?.name || code);
    return (
      code.toLowerCase().includes(q) ||
      displayName.toLowerCase().includes(q)
    );
  });

  const isRTL = lang === "ar";

  return (
    <div className="cc-dropdown" onClick={(e) => e.stopPropagation()}>
      {/* Search */}
      <div className="cc-dropdown-search">
        <Search size={14} color="#666" />
        <input
          autoFocus
          type="text"
          placeholder={lang === "en" ? "Search currency..." : "بحث عن عملة..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="cc-search-input"
          dir={isRTL ? "rtl" : "ltr"}
        />
      </div>

      {/* List */}
      <div className="cc-dropdown-list">
        {filtered.length === 0 && (
          <div className="cc-dropdown-empty">
            {lang === "en" ? "No results found" : "لا توجد نتائج"}
          </div>
        )}
        {filtered.map((code) => {
          const meta = CURRENCY_META[code];
          const isPopular = POPULAR_CURRENCIES.includes(code);
          const displayName = lang === "en" ? (meta?.nameEn || meta?.name || code) : (meta?.name || code);
          return (
            <button
              key={code}
              className={`cc-dropdown-item ${selected === code ? "active" : ""}`}
              onClick={() => { onSelect(code); onClose(); }}
            >
              <span className="cc-item-flag">{meta?.flag || "🌐"}</span>
              <span className="cc-item-code">{code}</span>
              <span className="cc-item-name">{displayName}</span>
              {isPopular && <span className="cc-item-popular">★</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const CurrencyConverter = ({ isOpen, onClose }) => {
  const { lang } = useLanguage();

  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [cachedAt, setCachedAt] = useState(null);
  const [online, setOnline] = useState(true);

  const [amount, setAmount] = useState("1");
  const [selectedCurrency, setSelectedCurrency] = useState("EGP");
  const [showDropdown, setShowDropdown] = useState(false);
  const [result, setResult] = useState(null);
  const [justRefreshed, setJustRefreshed] = useState(false);

  const dropdownRef = useRef(null);

  const isEN = lang === "en";

  // ── Load rates when sidebar opens ──
  useEffect(() => {
    if (!isOpen) return;
    loadRates(false);
  }, [isOpen]);

  // ── Close dropdown on outside click ──
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ── Auto-convert when amount or currency changes ──
  useEffect(() => {
    convert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, selectedCurrency, rates]);

  const loadRates = async (forceRefresh) => {
    setLoading(true);
    setError(null);
    try {
      const { rates: r, fromCache: fc, cachedAt: ca } = await getRates(forceRefresh);
      setRates(r);
      setFromCache(fc);
      setCachedAt(ca);
      setOnline(true);
      if (forceRefresh) {
        setJustRefreshed(true);
        setTimeout(() => setJustRefreshed(false), 2500);
      }
    } catch (err) {
      setError(isEN ? "Could not connect to the rates server" : "تعذّر الاتصال بخادم الأسعار");
      setOnline(false);
    } finally {
      setLoading(false);
    }
  };

  const convert = () => {
    if (!rates || !selectedCurrency) { setResult(null); return; }
    const num = parseFloat(amount);
    if (isNaN(num) || num < 0) { setResult(null); return; }
    const rate = rates[selectedCurrency];
    if (!rate) { setResult(null); return; }
    setResult(num * rate);
  };

  const meta = CURRENCY_META[selectedCurrency];
  const rate = rates ? rates[selectedCurrency] : null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`cc-overlay ${isOpen ? "active" : ""}`}
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <div className={`cc-sidebar ${isOpen ? "open" : ""}`}>
        {/* ── Header ── */}
        <div className="cc-header">
          <div className="cc-header-title">
            <ArrowLeftRight size={18} color="#ffbb00" />
            <span>{isEN ? "Currency Converter" : "محوّل العملات"}</span>
          </div>
          <button className="cc-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="cc-body">

          {/* Status bar */}
          <div className="cc-status-bar">
            <div className={`cc-status-dot ${online ? "online" : "offline"}`} />
            {online ? (
              <span className="cc-status-text">
                {fromCache
                  ? isEN
                    ? `Cached data — ${cachedAt ? timeSince(cachedAt, lang) : ""}`
                    : `بيانات مخزّنة — ${cachedAt ? timeSince(cachedAt, lang) : ""}`
                  : isEN ? "Rates updated now" : "أسعار محدّثة الآن"}
              </span>
            ) : (
              <span className="cc-status-text offline">
                {isEN ? "No connection" : "لا يوجد اتصال"}
              </span>
            )}
            <button
              className={`cc-refresh-btn ${loading ? "spinning" : ""} ${justRefreshed ? "refreshed" : ""}`}
              onClick={() => loadRates(true)}
              disabled={loading}
              title={isEN ? "Refresh rates" : "تحديث الأسعار"}
            >
              {justRefreshed ? (
                <CheckCircle size={14} color="#00ff88" />
              ) : (
                <RefreshCw size={14} />
              )}
            </button>
          </div>

          {/* ── Main converter card ── */}
          <div className="cc-card">

            {/* USD input (fixed) */}
            <div className="cc-field-label">
              <DollarSign size={13} color="#ffbb00" />
              <span>{isEN ? "Amount in US Dollar" : "المبلغ بالدولار الأمريكي"}</span>
              <span className="cc-fixed-badge">{isEN ? "Fixed" : "ثابت"}</span>
            </div>
            <div className="cc-input-row">
              <div className="cc-currency-chip usd">
                <span>🇺🇸</span>
                <span>USD</span>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="cc-amount-input"
                dir="ltr"
                placeholder="0"
              />
            </div>

            {/* Arrow divider */}
            <div className="cc-arrow-divider">
              <div className="cc-arrow-line" />
              <div className="cc-arrow-icon">
                <ArrowLeftRight size={16} color="#ffbb00" />
              </div>
              <div className="cc-arrow-line" />
            </div>

            {/* Target currency selector */}
            <div className="cc-field-label">
              <TrendingUp size={13} color="#e50000" />
              <span>{isEN ? "Target Currency" : "العملة المراد التحويل إليها"}</span>
            </div>
            <div className="cc-input-row" ref={dropdownRef}>
              {/* Currency picker button */}
              <button
                className="cc-currency-btn"
                onClick={() => setShowDropdown((v) => !v)}
              >
                <span className="cc-btn-flag">{meta?.flag || "🌐"}</span>
                <span className="cc-btn-code">{selectedCurrency}</span>
                <ChevronDown
                  size={13}
                  style={{
                    transform: showDropdown ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </button>

              {/* Result display */}
              <div className="cc-result-box">
                {loading ? (
                  <span className="cc-loading-dots">
                    <span /><span /><span />
                  </span>
                ) : error ? (
                  <span className="cc-result-error">—</span>
                ) : (
                  <span className="cc-result-value" dir="ltr">
                    {result !== null ? formatResult(result, lang) : "—"}
                  </span>
                )}
                <span className="cc-result-code">{selectedCurrency}</span>
              </div>

              {/* Dropdown */}
              {showDropdown && (
                <CurrencyDropdown
                  selected={selectedCurrency}
                  rates={rates}
                  onSelect={setSelectedCurrency}
                  onClose={() => setShowDropdown(false)}
                  lang={lang}
                />
              )}
            </div>

            {/* Rate info */}
            {rate && !loading && (
              <div className="cc-rate-info" dir="ltr">
                1 USD = {rate.toFixed(4)} {selectedCurrency}
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="cc-error-box">
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          {/* ── Cache disclaimer ── */}
          <div className="cc-disclaimer">
            <Clock size={12} color="#666" />
            <span>
              {isEN
                ? "Conversion may not be 100% accurate due to sudden currency rate changes"
                : "التحويل أحيانًا لا يكون دقيقًا 100% بسبب تغيّر سعر العملات المفاجئ"}
            </span>
          </div>

          {/* ── Cache info badge ── */}
          {fromCache && cachedAt && (
            <div className="cc-cache-badge">
              <div className="cc-cache-icon">
                <Clock size={11} />
              </div>
              <div className="cc-cache-text">
                <span className="cc-cache-title">
                  {isEN ? "Rates cached in database" : "أسعار مخزّنة في قاعدة البيانات"}
                </span>
                <span className="cc-cache-sub">
                  {isEN
                    ? `Last updated ${timeSince(cachedAt, lang)} — refreshes every 3 days`
                    : `آخر تحديث ${timeSince(cachedAt, lang)} — يتجدد كل 3 أيام`}
                </span>
              </div>
            </div>
          )}

          {/* ── Quick amounts ── */}
          <div className="cc-quick-section">
            <div className="cc-quick-label">{isEN ? "Quick amounts" : "مبالغ سريعة"}</div>
            <div className="cc-quick-btns">
              {[1, 3, 5, 10, 20, 50, 100].map((v) => (
                <button
                  key={v}
                  className={`cc-quick-btn ${amount === String(v) ? "active" : ""}`}
                  onClick={() => setAmount(String(v))}
                >
                  ${v}
                </button>
              ))}
            </div>
          </div>

          {/* ── Popular currencies quick preview ── */}
          {rates && (
            <div className="cc-popular-section">
              <div className="cc-popular-label">{isEN ? "Popular currencies" : "عملات شائعة"}</div>
              <div className="cc-popular-grid">
                {["EGP","SAR","AED","EUR","GBP","TRY"].map((code) => {
                  const m = CURRENCY_META[code];
                  const r = rates[code];
                  const amt = parseFloat(amount) || 1;
                  return (
                    <button
                      key={code}
                      className={`cc-popular-item ${selectedCurrency === code ? "active" : ""}`}
                      onClick={() => setSelectedCurrency(code)}
                    >
                      <span className="cc-pop-flag">{m?.flag}</span>
                      <span className="cc-pop-code">{code}</span>
                      <span className="cc-pop-val" dir="ltr">
                        {r ? formatResult(amt * r, lang) : "—"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default CurrencyConverter;
